import React, { useEffect, useState } from "react";
import { Alert, App, Button, Modal, Space, Spin, Tag, Typography } from "antd";
import request from "../../../utils/request";
import { useGlobal } from "../../../contexts/GlobalContext";
import { PodcastInfo, PodcastStatus, SPEAKER_TYPES } from "../../../types/podcast";

const { Paragraph, Text } = Typography;

interface PodcastPlayerProps {
  visible: boolean;
  onCancel: () => void;
  podcast: PodcastInfo | null;
}

const statusTextMap: Record<number, string> = {
  [PodcastStatus.Pending]: "等待中",
  [PodcastStatus.Processing]: "生成中",
  [PodcastStatus.Completed]: "已完成",
  [PodcastStatus.Failed]: "失败",
};

const formatDuration = (duration?: number) => {
  if (!duration || duration <= 0) {
    return "-";
  }

  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  return `${minutes}分${seconds.toString().padStart(2, "0")}秒`;
};

const PodcastPlayer: React.FC<PodcastPlayerProps> = ({
  visible,
  onCancel,
  podcast,
}) => {
  const { API_BASE_URL } = useGlobal();
  const { message } = App.useApp();
  const [resolvedAudioUrl, setResolvedAudioUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const resolveAudioUrl = async () => {
      if (!visible || !podcast) {
        setResolvedAudioUrl("");
        return;
      }

      if (podcast.audio_url) {
        setResolvedAudioUrl(podcast.audio_url);
        return;
      }

      if (podcast.status !== PodcastStatus.Completed) {
        setResolvedAudioUrl("");
        return;
      }

      setLoading(true);
      try {
        const response = await request.get(
          `${API_BASE_URL}/api/v3/podcasts/${podcast.id}/download`
        );

        if (response.data.code === 200 && response.data.data?.url) {
          setResolvedAudioUrl(response.data.data.url);
        } else {
          setResolvedAudioUrl("");
          message.error(response.data.msg || "获取播客音频失败");
        }
      } catch (error: any) {
        console.error("获取播客音频失败:", error);
        setResolvedAudioUrl("");
        message.error(error.response?.data?.msg || "获取播客音频失败");
      } finally {
        setLoading(false);
      }
    };

    resolveAudioUrl();
  }, [API_BASE_URL, message, podcast, visible]);

  const handleDownload = () => {
    if (!podcast || !resolvedAudioUrl) {
      message.warning("当前播客音频暂不可下载");
      return;
    }

    setDownloading(true);
    try {
      const link = document.createElement("a");
      link.href = resolvedAudioUrl;
      link.download = `${podcast.title || "podcast"}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      title="播客播放"
      open={visible}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      footer={[
        <Button key="download" onClick={handleDownload} loading={downloading}>
          下载音频
        </Button>,
        <Button key="close" onClick={onCancel}>
          关闭
        </Button>,
      ]}
    >
      {!podcast ? (
        <Alert type="info" showIcon message="暂无可播放的播客" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap>
            <Text strong>{podcast.title || "未命名播客"}</Text>
            <Tag
              color={
                podcast.status === PodcastStatus.Completed
                  ? "success"
                  : podcast.status === PodcastStatus.Failed
                  ? "error"
                  : "processing"
              }
            >
              {podcast.status_text || statusTextMap[podcast.status] || "未知状态"}
            </Tag>
            <Tag>
              {SPEAKER_TYPES[podcast.speaker_type as keyof typeof SPEAKER_TYPES] ||
                podcast.speaker_type ||
                "默认发音人"}
            </Tag>
          </Space>

          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text type="secondary">时长: {formatDuration(podcast.duration)}</Text>
            <Text type="secondary">
              创建时间: {new Date(podcast.created_at).toLocaleString("zh-CN")}
            </Text>
            {podcast.completed_at && (
              <Text type="secondary">
                完成时间: {new Date(podcast.completed_at).toLocaleString("zh-CN")}
              </Text>
            )}
          </Space>

          {podcast.error_msg && (
            <Alert type="error" showIcon message={podcast.error_msg} />
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spin />
            </div>
          ) : resolvedAudioUrl ? (
            <audio
              controls
              src={resolvedAudioUrl}
              style={{ width: "100%" }}
              preload="metadata"
            />
          ) : podcast.status === PodcastStatus.Completed ? (
            <Alert type="warning" showIcon message="播客已完成，但暂未获取到可播放音频" />
          ) : (
            <Alert
              type="info"
              showIcon
              message="播客仍在处理中，完成后可在此直接播放"
            />
          )}

          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            播放器已切换为 v3 资源播放模式，不再依赖旧版流式 WebSocket 接口。
          </Paragraph>
        </Space>
      )}
    </Modal>
  );
};

export default PodcastPlayer;
